/**
 * Onboarding API wrapper. The /onboarding/upload endpoint is multipart;
 * axios sets the multipart boundary automatically when given a FormData
 * body. CSRF + cookie credentials are applied by the shared engine.
 */
import { useMutation } from "@tanstack/react-query";
import type { UseMutationOptions } from "@tanstack/react-query";

import { engine } from "../engine.js";
import type { APIError } from "../http.js";

export interface OnboardingUploadInput {
  email: string;
  companyName: string;
  companySlug: string;
  companyCode?: string;
  // Optional company profile fields (camelCase here, snake_case on the wire).
  legalName?: string;
  contactEmail?: string;
  contactPhone?: string;
  websiteUrl?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateRegion?: string;
  postalCode?: string;
  countryCode?: string;
  defaultTimezone?: string;
  defaultCurrency?: string;
  logoFile?: File;
}

export interface OnboardingUploadResponse {
  tenantId: string;
  logoUrl: string | null;
}

export async function onboardingUpload(
  input: OnboardingUploadInput,
): Promise<OnboardingUploadResponse> {
  const form = new FormData();
  form.append("email", input.email);
  form.append("company_name", input.companyName);
  form.append("company_slug", input.companySlug);
  if (input.companyCode) form.append("company_code", input.companyCode);

  const optional: Array<[string, string | undefined]> = [
    ["legal_name", input.legalName],
    ["contact_email", input.contactEmail],
    ["contact_phone", input.contactPhone],
    ["website_url", input.websiteUrl],
    ["address_line1", input.addressLine1],
    ["address_line2", input.addressLine2],
    ["city", input.city],
    ["state_region", input.stateRegion],
    ["postal_code", input.postalCode],
    ["country_code", input.countryCode],
    ["default_timezone", input.defaultTimezone],
    ["default_currency", input.defaultCurrency],
  ];
  for (const [key, value] of optional) {
    if (value != null && value.trim().length > 0) form.append(key, value.trim());
  }

  if (input.logoFile) form.append("logo_file", input.logoFile);

  const res = await engine.post<OnboardingUploadResponse>(
    "/api/v2/onboarding/upload",
    form,
  );
  return res.data;
}

export function useOnboardingUploadMutation(
  options?: Omit<
    UseMutationOptions<OnboardingUploadResponse, APIError, OnboardingUploadInput>,
    "mutationFn"
  >,
) {
  return useMutation<OnboardingUploadResponse, APIError, OnboardingUploadInput>({
    ...options,
    mutationFn: onboardingUpload,
  });
}
